import React, { useEffect, useMemo, useRef, useState } from "react";

interface ColumnConfig<T = any> {
  key?: string; // puede faltar (ej.: "New Value")
  label: string;
  subTitle?: string;
  // render(row, onEdit)
  render: (row: T, onEdit: (id: string, newValue: string) => void) => React.ReactNode;
}

interface Props<T = any> {
  data: T[];
  configs: ColumnConfig<T>[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: (id: string, newValue: string) => void;
  onVisibleRowsChange?: (visible: T[]) => void;
  resetPageTrigger?: boolean;
  processingIds?: string[]; // ids en procesamiento por fila
  editedIds?: string[];     // ids editados para resaltar
}

const Table = <T extends { id: string }>({
  data,
  configs,
  onApprove,
  onReject,
  onEdit,
  onVisibleRowsChange,
  resetPageTrigger,
  processingIds = [],
  editedIds = [],
}: Props<T>) => {
  const lastVisibleIds = useRef<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  // Reiniciar solo cuando viene el trigger explícito desde arriba
  useEffect(() => {
    if (resetPageTrigger) {
      setCurrentPage(1);
    }
  }, [resetPageTrigger]);

  // Ordenamiento seguro (solo si hay key válida)
  const sortedData = useMemo(() => {
    if (!sortConfig || !sortConfig.key) return data;

    const key = sortConfig.key as keyof T;

    // clonar para no mutar prop
    const copy = [...data];
    copy.sort((a, b) => {
      const aValue = (a as any)[key];
      const bValue = (b as any)[key];

      // null/undefined al final
      if (aValue == null || bValue == null) {
        if (aValue == null && bValue == null) return 0;
        return aValue == null ? 1 : -1;
      }

      // comparar números si ambos son números
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // fallback a string
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortConfig.direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
    return copy;
  }, [data, sortConfig]);

  // Paginación
  const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  useEffect(() => {
    // Si el total de páginas disminuyó y quedamos fuera de rango, ajustamos.
    const newTotal = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
    if (currentPage > newTotal) {
      setCurrentPage(newTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData.length, rowsPerPage]);

  // Notificar filas visibles (para resolver valores solo de lo visible)
  useEffect(() => {
    const currentIds = currentData.map((item) => item.id).join(",");
    const lastIds = lastVisibleIds.current.join(",");
    if (currentIds !== lastIds && onVisibleRowsChange) {
      onVisibleRowsChange(currentData);
      lastVisibleIds.current = currentData.map((item) => item.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleSort = (key?: string) => {
    if (!key) return; // columnas sin key no son ordenables
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const renderedRows = currentData.map((row) => {
    const isBusy = processingIds.includes(row.id);
    const isEdited = editedIds.includes(row.id);

    return (
      <tr key={row.id} className={isEdited ? "bg-yellow-50" : ""}>
        {configs.map((config) => (
          <td
            key={(config.key ?? config.label) + "_" + row.id}
            className="p-4 whitespace-nowrap text-sm font-normal text-gray-900 hover:bg-gray-50 align-top"
            title={config.subTitle}
          >
            {config.render(row, onEdit)}
          </td>
        ))}

        <td className="p-4 whitespace-nowrap text-center align-middle">
          <button
            onClick={() => onApprove(row.id)}
            disabled={isBusy}
            title={isBusy ? "Procesando..." : "Aprobar"}
            className={`mr-2 p-2 rounded-full ${
              isBusy
                ? "opacity-50 cursor-not-allowed bg-green-100"
                : "text-green-500 hover:text-green-700 bg-green-100"
            }`}
            aria-label="Approve"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            onClick={() => onReject(row.id)}
            disabled={isBusy}
            title={isBusy ? "Procesando..." : "Rechazar"}
            className={`p-2 rounded-full ${
              isBusy
                ? "opacity-50 cursor-not-allowed bg-red-100"
                : "text-red-500 hover:text-red-700 bg-red-100"
            }`}
            aria-label="Reject"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </td>
      </tr>
    );
  });

  const renderedHeaders = configs.map((config) => {
    const sortable = !!config.key;
    const isActive = sortConfig?.key === config.key;

    return (
      <th
        key={config.key ?? config.label}
        scope="col"
        className={`p-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider ${
          sortable ? "cursor-pointer" : ""
        } min-w-[160px]`}
        onClick={() => (sortable ? handleSort(config.key) : undefined)}
        title={config.subTitle}
      >
        <div className="flex items-center gap-1">
          <span>{config.label}</span>
          {sortable && isActive && (
            <span>{sortConfig?.direction === "asc" ? "↑" : "↓"}</span>
          )}
        </div>
      </th>
    );
  });

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6 xl:p-8">
      {/* Controles superiores */}
      <div className="mb-4 flex justify-end items-center gap-2">
        <label htmlFor="rowsPerPage" className="text-sm text-gray-700">
          rows per page:
        </label>
        <select
          id="rowsPerPage"
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
          className="text-sm border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="Filas por página"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
        </select>
      </div>

      {/* Tabla con scroll horizontal y encabezado sticky */}
      <div className="overflow-x-auto p-1">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-white sticky top-0 z-10">
            <tr>
              {renderedHeaders}
              <th
                scope="col"
                className="p-4 text-center text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]"
              >
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">{renderedRows}</tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="flex justify-between items-center mt-4">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          title={currentPage === 1 ? "Primera página" : "Página anterior"}
          className={`px-4 py-2 text-sm font-medium ${
            currentPage === 1 ? "text-gray-400" : "text-blue-500 hover:text-blue-700"
          }`}
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          title={currentPage === totalPages ? "Última página" : "Página siguiente"}
          className={`px-4 py-2 text-sm font-medium ${
            currentPage === totalPages ? "text-gray-400" : "text-blue-500 hover:text-blue-700"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Table;
